import addDays from 'date-fns/addDays'
import Fuse from 'fuse.js'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'

import arriereBackoffice, {
  BookingStatus,
  HomeOperationsReadinessStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate } from '@avantstay/backoffice-core'

import { ListItem } from '@new/__global/components/FastTapeChart/__types'
import { TAPECHART_CACHE_TIMES } from '@new/__global/components/FastTapeChart/config/requestCacheTimes'
import { SortOrderKey } from '@new/__global/components/FiltersFramework'
import { noEciLco, VALID_BOOKING_STATUSES } from '@new/__global/constants/calendar'
import { cachedRequest, shareInFlight } from '@new/__global/utils/requestCache'
import { RoomTypeListArgs } from '@new/domains/hotelTapeChart/HotelTapeChart.types'
import { sortListingResults } from '@new/domains/hotelTapeChart/stores/unitsSorting'

import { assetFragment } from '@arriere/modules/fragments/asset'
import { hotelAmenitiesFragment } from '@arriere/modules/hotelLayout'
import { amenitySelectionFragment } from '@arriere/modules/hotels'
import {
  HomesWithCalendarInput,
  HotelUnitsForBookingInput,
  HotelUnitsForCreationInput,
  PeriodInput,
} from '@arriere/types/Properties.types'

interface ListingUnitProps {
  name: string
  id: string
  hotelRoomTypeId: string
  unitNumber?: string
  rank?: number
  maxOccupancy?: number
  region?: {
    name: string
  }
}

export type UnitProps = ListItem<ListingUnitProps>

interface UnitFilters {
  operationReadinessStatus?: HomeOperationsReadinessStatus[]
  checkInPeriod?: {
    start: string
    end: string
  }
  includeAvailableOnCheckInPeriod?: boolean
}

const bookingsFragment = {
  id: true,
  hash: true,
  source: true,
  homeId: true,
  guestName: true,
  notes: true,
  isRoomAssigned: true,
  status: true,
  activeServicesCount: true,
  issues: true,
  securityDepositStatus: true,
  hotelRoomType: {
    id: true,
    roomKind: true,
  },
  period: { start: true, end: true },
  folio: {
    summary: {
      paymentBalanceStatus: true,
    },
  },
  checkInTiming: {
    originalTime: true,
    effectiveTime: true,
  },
  checkOutTiming: {
    originalTime: true,
    effectiveTime: true,
  },
}

const hasOnlyHotelId = (filters?: Record<string, any>, unitFilters?: UnitFilters) =>
  Object.entries(filters || {}).every(([key, value]) => {
    if (key === 'hotelId') return true
    return value === undefined
  }) &&
  (!unitFilters || Object.values(unitFilters).every(value => value === undefined))

const sharedFetchUnits = async ({
  alias,
  filters,
  unitFilters,
}: {
  filters?: Record<string, any>
  unitFilters?: UnitFilters
  alias?: string
}) => {
  const { search: roomTypes } = await arriereBackoffice.queries.hotelRoomType({
    __alias: alias ?? 'fetchAllRoomTypes',
    search: {
      __args: {
        hotelId: filters?.hotelId,
      },
      hotelRooms: {
        hotelRoomTypeId: true,
        name: true,
        rank: true,
        maxOccupancy: true,
        unitSearch: {
          __args: { filter: { ...unitFilters } },
          id: true,
          name: true,
          unitNumber: true,
          region: {
            name: true,
          },
        },
      },
    },
  })

  return roomTypes.hotelRooms.flatMap(roomType => {
    return (
      roomType.unitSearch?.map(unit => {
        const tapeContextBasicInfo = {
          group: {
            name: roomType.name,
            id: roomType?.hotelRoomTypeId,
          },
          secondaryListItemId: roomType.hotelRoomTypeId,
          itemKey: unit.id,
        }

        const name = unit.unitNumber?.toString() || unit.name

        return {
          ...unit,
          name,
          rank: roomType.rank,
          maxOccupancy: roomType.maxOccupancy,
          hotelRoomTypeId: roomType.hotelRoomTypeId,
          ...tapeContextBasicInfo,
        }
      }) || []
    )
  })
}

export const hotelArriere = {
  fetchHotelInfo: cachedRequest({
    request: async ({ hotelId }: { hotelId: string }) => {
      const { get: hotelInfo } = await arriereBackoffice.queries.hotel
        .raw({
          __alias: 'getHotelInfo',
          get: {
            __args: {
              hotelId,
            },
            hotel: {
              id: true,
              name: true,
            },
            hotelRooms: {
              hotelRoomTypeId: true,
              name: true,
              bedsCount: true,
              bedroomsCount: true,
              bathroomsCount: true,
              maxOccupancy: true,
              rank: true,
              units: {
                id: true,
                name: true,
                title: true,
                isAncestor: true,
                isSuccessor: true,
                region: {
                  name: true,
                },
                rooms: {
                  kind: true,
                  sleepingPlaces: {
                    amenity: {
                      name: true,
                      id: true,
                    },
                    roomId: true,
                    quantity: true,
                  },
                },
              },
            },
          },
        })
        .then(({ data, errors }) => {
          if (errors && errors.length) {
            throw new Error(errors[errors?.length - 1]?.message)
          }

          return data
        })

      return {
        ...hotelInfo?.hotel,
        roomTypes: hotelInfo?.hotelRooms,
      }
    },
    maxAge: TAPECHART_CACHE_TIMES.hotelInfo,
  }),

  fetchHotelSummary: shareInFlight(async ({ hotelId }: { hotelId: string }) => {
    const { get: hotelInfo } = await arriereBackoffice.queries.hotel
      .raw({
        __alias: 'getHotelSummary',
        get: {
          __args: {
            hotelId,
          },
          hotelRooms: {
            hotelRoomTypeId: true,
            checkInsSummary: {
              __args: { checkInDate: formatDate(new Date()) },
              assignedTotal: true,
              checkedInTotal: true,
              checkInDate: true,
              checkInsTotal: true,
              notAssignedTotal: true,
              notCheckedInTotal: true,
            },
          },
        },
      })
      .then(({ data, errors }) => {
        if (errors && errors.length) {
          throw new Error(errors[errors?.length - 1]?.message)
        }

        return data
      })

    return hotelInfo?.hotelRooms
  }),

  fetchBookings: shareInFlight(
    async ({
      startDate,
      endDate,
      homeIds,
      assignedRoom,
      filters,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
      assignedRoom?: boolean
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters?: Record<string, any>
    }) => {
      const { data: bookings, errors } = await arriereBackoffice.queries.bookings.raw({
        __alias: 'hotelTapeChartBookings',
        __args: {
          from: startDate,
          to: endDate,
          status: VALID_BOOKING_STATUSES,
          homeIds,
          pagination: { page: 1, size: 99999 },
          assignedRoom,
          ...filters,
        },
        results: {
          id: true,
          hash: true,
          source: true,
          homeId: true,
          guestName: true,
          notes: true,
          isRoomAssigned: true,
          status: true,
          activeServicesCount: true,
          issues: true,
          securityDepositStatus: true,
          hotelRoomType: {
            id: true,
            roomKind: true,
          },
          period: { start: true, end: true },
          folio: {
            summary: {
              paymentBalanceStatus: true,
            },
          },
          checkInTiming: {
            originalTime: true,
            effectiveTime: true,
          },
          checkOutTiming: {
            originalTime: true,
            effectiveTime: true,
          },
        },
      })

      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return orderBy(bookings.results, it => it.checkInTiming.effectiveTime)
    },
  ),
  fetchUnassignedBookings: shareInFlight(
    async ({
      homeIds,
      filters,
      startDate,
      endDate,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters?: Record<string, any>
    }) => {
      const { data: bookings, errors } = await arriereBackoffice.queries.bookings.raw({
        __alias: 'hotelTapeChartUnassignedBookings',
        __args: {
          from: startDate,
          to: endDate,
          status: [
            BookingStatus.booked,
            BookingStatus.checkedIn,
            BookingStatus.requested,
            BookingStatus.modified,
          ],
          homeIds,
          pagination: { page: 1, size: 99999 },
          assignedRoom: false,
          ...filters,
        },
        results: {
          ...bookingsFragment,
        },
      })

      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return orderBy(bookings.results, it => it.checkInTiming.effectiveTime)
    },
  ),

  fetchTapeChartBlocks: shareInFlight(
    async ({
      startDate,
      endDate,
      homeIds,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
    }) => {
      const { searchAvailabilityBlocks } = await arriereBackoffice.queries.tapeChart({
        __alias: 'hotelTapeChartBlocks',
        __args: {
          period: {
            start: startDate,
            end: endDate,
          },
          homeIds,
        },
        searchAvailabilityBlocks: {
          id: true,
          homeId: true,
          period: { start: true, end: true },
          source: true,
          createdAt: true,
          createdBy: true,
          updatedAt: true,
          updatedBy: true,
          note: true,
          notificationSetting: {
            notifyAuthor: true,
            notifyRelocationTeam: true,
            notifyFieldOpsTeam: true,
          },
          parent: {
            bookingId: true,
            id: true,
            homeId: true,
            period: {
              start: true,
              end: true,
            },
            source: true,
          },
          expiresAt: true,
          booking: {
            detailedCheckIn: {
              effectiveTime: true,
            },
            detailedCheckOut: {
              effectiveTime: true,
            },
            hash: true,
            guestName: true,
            home: {
              name: true,
            },
          },
        },
      })

      return uniqBy(
        searchAvailabilityBlocks,
        it => `${it.homeId},${it.period.start},${it.period.end},${it.source},${it.note}`,
      ).filter(noEciLco)
    },
  ),

  fetchTapeChartRates: cachedRequest({
    request: async ({
      startDate,
      endDate,
      homeIds,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
    }) => {
      const { searchHomeDatedPrices } = await arriereBackoffice.queries.tapeChart({
        __alias: 'hotelTapeChartRates',
        __args: {
          period: {
            start: startDate,
            end: endDate,
          },
          homeIds,
        },
        searchHomeDatedPrices: {
          date: true,
          homeId: true,
          minStay: true,
          maxStay: true,
          rate: true,
          checkInAllowed: true,
          checkOutAllowed: true,
        },
      })

      return groupBy(searchHomeDatedPrices, it => it.homeId)
    },
    maxAge: TAPECHART_CACHE_TIMES.rates,
  }),

  fetchListings: async ({
    filters,
    page = 1,
    pageSize = 9999,
    sortBy,
    sortOrder,
    terms,
    unitFilters,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters?: Record<string, any>
    page?: number
    pageSize?: number
    terms: string
    unitFilters?: UnitFilters
    sortBy?: string
    sortOrder?: SortOrderKey
  }) => {
    // Use memoized version only if there's just hotelId filter
    const homesList = hasOnlyHotelId(filters, unitFilters)
      ? await hotelArriere.fetchMemoizedAllListings({ hotelId: filters?.hotelId })
      : await hotelArriere.fetchAllListings({
          filters,
          unitFilters,
        })

    const mappedHomeList = homesList?.map(it => ({
      ...it,
      terms: [it.name, it.unitNumber, ...it.name.split(/\s+-\s+/), ...it.name.split(/[\s-]+/)],
    }))

    const { searchableList, rawList } = {
      rawList: mappedHomeList,
      searchableList: new Fuse(mappedHomeList, {
        keys: ['terms'],
        shouldSort: false,
        threshold: 0.25,
        location: 0,
        distance: 3,
      }),
    }

    const results = !terms
      ? rawList
      : searchableList
          .search({
            $or: [
              { terms: terms.trim() },
              {
                $and: terms
                  .trim()
                  .split(/\W+/)
                  .map(it_1 => ({ terms: it_1 })),
              },
            ],
          })
          .map(it_2 => it_2.item)

    const filteredResults = results

    const offset = (page - 1) * pageSize

    const sortedResults =
      sortBy && sortOrder ? sortListingResults(filteredResults, sortBy, sortOrder) : filteredResults

    const currentPageOfResults = sortedResults.slice(offset, offset + pageSize)

    return {
      total: results.length,
      results: currentPageOfResults,
      sortedRawResults: sortedResults,
      rawResults: rawList,
    }
  },

  fetchAllListings: async ({
    alias,
    filters = {},
    unitFilters = {},
  }: {
    alias?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters?: Record<string, any>
    unitFilters?: UnitFilters
  }) => {
    return sharedFetchUnits({ filters, unitFilters, alias })
  },

  fetchMemoizedAllListings: cachedRequest({
    request: async ({ hotelId }: { hotelId: string }) =>
      sharedFetchUnits({ filters: { hotelId }, alias: 'fetchAllRoomTypes_No_Filter' }),
    maxAge: TAPECHART_CACHE_TIMES.hotelRooms,
  }),

  fetchHomesListExtendedInfo: cachedRequest({
    request: async ({ homeIds }: { homeIds: string[] }) => {
      const { results: homes } = await arriereBackoffice.queries.tapeChartPaginatedHomes({
        __alias: 'fetchAllHomesExtendedInfo',
        __args: {
          homeIds,
          pagination: {
            page: 1,
            size: 9999,
          },
        },
        results: {
          id: true,
          home: {
            operationReadinessStatus: {
              status: true,
              othersCases: true,
              othersCasesDetails: {
                cases: {
                  isHighOrUrgentPriority: true,
                  workOrderId: true,
                },
                completed: {
                  workOrderId: true,
                },
                status: true,
                total: true,
                referencePeriod: {
                  end: true,
                  start: true,
                },
              },
              cleanDetails: {
                status: true,
                cases: {
                  workOrderId: true,
                },
                total: true,
                referencePeriod: {
                  end: true,
                  start: true,
                },
              },
              inspectedDetails: {
                status: true,
                cases: {
                  workOrderId: true,
                },
                total: true,
              },
            },
            operationTags: {
              currentBookingId: true,
              short: true,
            },
          },
        },
      })

      return homes
    },
    maxAge: TAPECHART_CACHE_TIMES.readiness,
  }),

  clearHotelData: (): void => {
    hotelArriere.fetchHotelInfo.clear()
    hotelArriere.fetchHotelSummary.clear()
    hotelArriere.fetchMemoizedAllListings.clear()
    hotelArriere.fetchHomesListExtendedInfo.clear()
  },
}

export const getHotelOmniSearchResults = async (hotelId: string, term: string) => {
  return arriereBackoffice.queries.hotel
    .raw({
      __alias: 'hotelTapeChartSearchResults',
      bookingOmniSearch: {
        __args: { hotelId, term },
        total: true,
        results: {
          id: true,
          checkInTiming: {
            effectiveTime: true,
          },
          checkOutTiming: {
            effectiveTime: true,
          },
          guestName: true,
          hash: true,
          home: {
            name: true,
            unitNumber: true,
            region: {
              name: true,
            },
          },
          isRoomAssigned: true,
          hotelRoomType: {
            id: true,
            title: true,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data?.bookingOmniSearch
    })
}

export const hotelRoomTypeCalendar = async (
  hotelRoomTypeId: string,
  period: { start: string; end: string },
) => {
  const { data, errors } = await arriereBackoffice.queries.hotelRoomType.raw({
    __alias: 'hotelRoomTypeCalendar',
    get: {
      __args: { hotelRoomTypeId },
      calendar: {
        __args: {
          period,
        },
        date: true,
        minStay: true,
        rate: true,
        status: true,
        availabilityCount: true,
        restrictions: {
          checkInAllowed: true,
          checkOutAllowed: true,
        },
      },
    },
  })
  if (errors && errors.length) {
    throw new Error(errors[errors?.length - 1]?.message)
  }
  return data?.get?.calendar
}

export const getRoomsTypeListWithCalendar = async ({
  from,
  to,
  hotelId,
}: HomesWithCalendarInput) => {
  if (!hotelId) {
    return Promise.reject('hotelId is missing')
  }

  return arriereBackoffice.queries.hotelRoomType
    .raw({
      __alias: 'getRoomsTypeListWithCalendar',
      list: {
        __args: { hotelId },
        hotelRoomTypeId: true,
        name: true,
        title: true,
        maxOccupancy: true,
        rank: true,
        units: {
          id: true,
        },
        calendar: {
          __args: {
            period: {
              start: from,
              end: to,
            },
          },
          date: true,
          status: true,
          rate: true,
          minStay: true,
          availabilityCount: true,
          restrictions: {
            checkInAllowed: true,
            checkOutAllowed: true,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data?.list?.map(roomType => ({ ...roomType, id: roomType.hotelRoomTypeId }))
    })
}

const batchRoomAssignmentFragment = {
  reason: true,
  booking: {
    guestName: true,
    hash: true,
    checkInTiming: {
      effectiveTime: true,
    },
    hotelRoomType: {
      roomKind: true,
    },
  },
  fromUnit: {
    name: true,
    unitNumber: true,
  },
  toUnit: {
    name: true,
    unitNumber: true,
  },
}

const autoAssignFragment = {
  checkInsTotal: true,
  checkInDate: true,
  assignmentsTotal: true,
  pendingAssignmentsTotal: true,
  assignments: batchRoomAssignmentFragment,
  pendingAssignments: {
    reason: true,
    booking: {
      guestName: true,
      hash: true,
      checkInTiming: {
        effectiveTime: true,
      },
      hotelRoomType: {
        roomKind: true,
      },
    },
  },
  details: {
    swaps: {
      booking: {
        guestName: true,
        hash: true,
      },
      fromUnit: {
        name: true,
        unitNumber: true,
      },
      toUnit: {
        name: true,
        unitNumber: true,
      },
      hasEci: true,
      hasLco: true,
    },
    total: true,
  },
  failureDetails: {
    total: true,
    swaps: {
      bookingId: true,
      homeId: true,
      hasEci: true,
      hasLco: true,
      conflictWith: {
        blockId: true,
        bookingId: true,
        period: { start: true, end: true },
        homeId: true,
        label: true,
        hasEci: true,
        hasLco: true,
      },
    },
  },
}

export const previewHotelAutoAssign = async (hotelRoomTypeId: string) => {
  return arriereBackoffice.queries.previewHotelRoomAssigmentBatch
    .raw({
      __alias: 'previewHotelAutoAssign',
      __args: { hotelRoomTypeId },
      ...autoAssignFragment,
    })
    .then(({ data, errors }) => {
      if (errors && errors.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data
    })
}

export const getRoomTypeDetails = async (hotelRoomTypeId: string) => {
  return arriereBackoffice.queries.hotelRoomType
    .raw({
      __alias: 'getRoomTypeDetails',
      get: {
        __args: { hotelRoomTypeId },
        hotelRoomTypeId: true,
        name: true,
        description: true,
        units: {
          id: true,
          unitNumber: true,
          name: true,
        },
        gallery: {
          ...assetFragment,
        },
        roomAmenities: {
          ...hotelAmenitiesFragment,
        },
        sleepingPlacesAmenities: {
          ...hotelAmenitiesFragment,
        },
        checkInsSummary: {
          __args: { checkInDate: formatDate(new Date()) },
          checkInsTotal: true,
          checkedInTotal: true,
          notCheckedInTotal: true,
          assignedTotal: true,
          notAssignedTotal: true,
        },
        availableUnitsCount: {
          __args: {
            period: {
              start: formatDate(new Date()),
              end: formatDate(addDays(new Date(), 1)),
            },
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data.get
    })
}

export const getHotelAmenities = async (hotelId: string) => {
  return arriereBackoffice.queries.hotel
    .raw({
      __alias: 'getHotelAmenities',
      get: {
        __args: { hotelId },
        hotel: {
          aggregatedAmenities: {
            roomTypeIds: true,
            hotelAmenity: {
              ...amenitySelectionFragment,
            },
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data.get?.hotel?.aggregatedAmenities
    })
}

export const getHotelAmenitiesList = async (hotelId: string) => {
  return arriereBackoffice.queries.hotelRoomType
    .raw({
      __alias: 'getHotelAmenitiesList',
      search: {
        __args: { hotelId },
        amenityFacet: {
          amenity: { id: true, name: true },
          count: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data.search?.amenityFacet
    })
}

export const getRoomTypeList = async ({
  hotelId,
  amenityIds,
  period,
  alias,
}: RoomTypeListArgs & { alias?: string }) => {
  return arriereBackoffice.queries.hotelRoomType
    .raw({
      __alias: alias || 'getRoomTypeList',
      search: {
        __args: { hotelId, amenityIds },
        hotelRooms: {
          hotelRoomTypeId: true,
          name: true,
          maxOccupancy: true,
          bedroomsCount: true,
          bathroomsCount: true,
          bedsCount: true,
          ...(period
            ? {
                availableUnitsCount: {
                  __args: {
                    period,
                  },
                },
              }
            : {}),
          referenceUnit: {
            area: true,
            rooms: {
              sleepingPlaces: {
                amenity: {
                  name: true,
                  id: true,
                },
                quantity: true,
              },
            },
          },
          gallery: {
            ...assetFragment,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data?.search?.hotelRooms
    })
}

const hotelUnitFragment = ({ end, start }: PeriodInput) => ({
  assignPriority: true,
  units: {
    home: {
      calendar: {
        __args: { from: start ?? '', to: end ?? '' },
        date: true,
        minStay: true,
        season: true,
        rate: true,
        status: true,
        restrictions: {
          checkInAllowed: true,
          checkOutAllowed: true,
        },
      },
      details: {
        onBoardingStatus: true,
      },
      hotelRoomType: { hotelRoomTypeId: true, name: true },
      id: true,
      lifecycle: {
        guestReadinessStatus: true,
        riskLevel: true,
      },
      maxOccupancy: true,
      name: true,
      operationReadinessStatus: {
        status: true,
      },
      region: {
        name: true,
        timezone: true,
      },
      revenueModel: true,
      sourceListed: {
        website: true,
      },
      unitNumber: true,
    },
    info: {
      isIndividualHomeowner: true,
      score: true,
    },
    isAvailable: true,
  },
})

export const getHotelUnitsForBooking = async ({
  bookingId,
  hotelRoomTypeId,
  end,
  start,
}: HotelUnitsForBookingInput) => {
  return arriereBackoffice.queries
    .hotelRoomType({
      __alias: 'getHotelUnitsForBooking',
      calendarHotelUnit: {
        __args: { bookingId, hotelRoomTypeId },
        ...hotelUnitFragment({ end, start }),
      },
    })
    .then(data => data.calendarHotelUnit)
}

export const getHotelUnitsForCreation = async ({
  end,
  start,
  hotelRoomTypeId,
}: HotelUnitsForCreationInput) => {
  return arriereBackoffice.queries
    .hotelRoomType({
      __alias: 'getHotelUnitsForCreation',
      calendarHotelUnitForCreation: {
        __args: { hotelRoomTypeId, period: { end, start } },
        ...hotelUnitFragment({ end, start }),
      },
    })
    .then(data => data.calendarHotelUnitForCreation)
}

export const getHotelUnitsForTapechartMenu = async ({
  end,
  start,
  hotelRoomTypeId,
}: HotelUnitsForCreationInput) => {
  return arriereBackoffice.queries
    .hotelRoomType({
      __alias: 'getHotelUnitsForTapechartMenu',
      calendarHotelUnitForCreation: {
        __args: { hotelRoomTypeId, period: { end, start } },
        assignPriority: true,
        units: {
          home: {
            hotelRoomType: { hotelRoomTypeId: true, name: true },
            id: true,
            name: true,
            operationReadinessStatus: {
              status: true,
            },
            region: {
              name: true,
              timezone: true,
            },
            unitNumber: true,
          },
          info: {
            isIndividualHomeowner: true,
            score: true,
          },
          isAvailable: true,
        },
      },
    })
    .then(data => data.calendarHotelUnitForCreation)
}

// Mutations

export const assignRoomsToBookingsCheckInToday = (hotelRoomTypeId: string) => {
  return arriereBackoffice.mutations.booking({
    __alias: 'assignRoomsToBookingsCheckInToday',
    assignRoomsToBookingsCheckInToday: {
      __args: { hotelRoomTypeId },
      checkInsTotal: true,
    },
  })
}
